import { useEffect, useRef, useState } from 'react';
import { useGlobal } from '../context/GlobalContext';
import { triggerFaqRegeneration, getFaqConfig } from '../lib/strapi';
import styles from './FaqAdmin.module.css';

const ADMIN_PASSWORD = import.meta.env.VITE_FAQ_ADMIN_PASSWORD;
const SESSION_KEY = 'faq-admin:authed';
// El job descarga chatmessages de Flowise, genera con OpenAI y traduce; puede
// tardar un par de minutos. Timeout generoso para no cancelar de golpe.
const REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

// Convierte lastRunResult (string JSON o texto) en algo legible.
const parseRunResult = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return { raw: String(raw) };
  }
};

const FaqAdmin = () => {
  const { companyInfo } = useGlobal();
  const companyName = companyInfo?.companyName || import.meta.env.VITE_APP_NAME || 'Koppi';

  const [authed, setAuthed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Panel state
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Login state
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');

  const timerRef = useRef(null);
  const controllerRef = useRef(null);

  // noindex,nofollow mientras el panel está montado (no pasa por applySeoMetadata).
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const prev = existing?.getAttribute('content') || null;
    let meta = existing;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex,nofollow');
    return () => {
      if (prev) {
        meta.setAttribute('content', prev);
      } else {
        meta.remove();
      }
    };
  }, []);

  // Carga la config del último run al autenticarse.
  useEffect(() => {
    if (!authed) return undefined;
    let cancelled = false;
    const load = async () => {
      setConfigLoading(true);
      const data = await getFaqConfig();
      if (!cancelled) {
        setConfig(data);
        setConfigLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [authed]);

  // Cronómetro durante la ejecución del job. Solo arranca/para el interval;
  // el reseteo de `elapsed` lo hace handleRegenerate antes de empezar.
  useEffect(() => {
    if (!submitting) return undefined;
    timerRef.current = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [submitting]);

  const handleLogin = (event) => {
    event.preventDefault();
    if (!passwordInput) {
      setLoginError('Ingresa la contraseña.');
      return;
    }
    if (passwordInput === ADMIN_PASSWORD) {
      setAuthed(true);
      setLoginError('');
      setPasswordInput('');
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        // sessionStorage puede fallar en modo privado; no bloquea el flujo.
      }
    } else {
      setLoginError('Contraseña incorrecta.');
    }
  };

  const handleLogout = () => {
    setAuthed(false);
    setResult(null);
    setError(null);
    setConfig(null);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // noop
    }
  };

  const handleRegenerate = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    setElapsed(0);

    const controller = new AbortController();
    controllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await triggerFaqRegeneration({ signal: controller.signal });
      setResult(response?.data ?? response);
      // Refresca la config para mostrar el nuevo lastRunAt/lastRunResult.
      const freshConfig = await getFaqConfig();
      setConfig(freshConfig);
    } catch (err) {
      setError(err?.message || 'Ocurrió un error al regenerar las FAQs.');
    } finally {
      clearTimeout(timeoutId);
      controllerRef.current = null;
      setSubmitting(false);
    }
  };

  // ─── Estado 1: tool no configurada ────────────────────────────────────────
  if (!ADMIN_PASSWORD) {
    return (
      <div className={styles.faqAdmin}>
        <div className={styles.faqAdmin__card}>
          <h1 className={styles.faqAdmin__title}>Admin FAQ</h1>
          <p className={styles.faqAdmin__muted}>
            Tool no configurada. Define
            <code className={styles.faqAdmin__code}> VITE_FAQ_ADMIN_PASSWORD </code>
            en el entorno para habilitar este panel.
          </p>
        </div>
      </div>
    );
  }

  // ─── Estado 2: no autenticado ─────────────────────────────────────────────
  if (!authed) {
    return (
      <div className={styles.faqAdmin}>
        <form className={styles.faqAdmin__card} onSubmit={handleLogin}>
          <h1 className={styles.faqAdmin__title}>Admin FAQ — {companyName}</h1>
          <p className={styles.faqAdmin__muted}>Acceso restringido al equipo.</p>

          <label className={styles.faqAdmin__label} htmlFor="faq-admin-password">
            Contraseña
          </label>
          <input
            id="faq-admin-password"
            type="password"
            className={styles.faqAdmin__input}
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />

          {loginError && <p className={styles.faqAdmin__errorText}>{loginError}</p>}

          <button type="submit" className={styles.faqAdmin__button}>
            Entrar
          </button>
        </form>
      </div>
    );
  }

  // ─── Estado 3: panel autenticado ──────────────────────────────────────────
  const runResult = parseRunResult(config?.lastRunResult);

  return (
    <div className={styles.faqAdmin}>
      <div className={styles.faqAdmin__container}>
        <header className={styles.faqAdmin__header}>
          <div>
            <h1 className={styles.faqAdmin__title}>Admin FAQ — {companyName}</h1>
            <p className={styles.faqAdmin__muted}>
              Disparar la regeneración de preguntas frecuentes desde el chatbot.
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className={styles.faqAdmin__linkButton}
          >
            Cerrar sesión
          </button>
        </header>

        <div className={styles.faqAdmin__notice}>
          <strong>⚠️ Requiere publicación manual.</strong> El job deja el contenido
          como borrador en Strapi. Entra al admin de Strapi y publica la página
          <code className={styles.faqAdmin__code}> preguntas-frecuentes </code>
          para que los cambios sean visibles en el sitio.
        </div>

        {/* Sección: última regeneración */}
        <section className={styles.faqAdmin__section}>
          <h2 className={styles.faqAdmin__subtitle}>Última regeneración</h2>
          {configLoading ? (
            <p className={styles.faqAdmin__muted}>Cargando…</p>
          ) : config ? (
            <dl className={styles.faqAdmin__grid}>
              <div className={styles.faqAdmin__gridRow}>
                <dt>Última ejecución</dt>
                <dd>{formatDate(config.lastRunAt)}</dd>
              </div>
              <div className={styles.faqAdmin__gridRow}>
                <dt>Cron activo</dt>
                <dd>{config.enabled ? 'Sí' : 'No'}</dd>
              </div>
              <div className={styles.faqAdmin__gridRow}>
                <dt>Días de regeneración</dt>
                <dd>{config.regenerationDays || '—'}</dd>
              </div>
              <div className={styles.faqAdmin__gridRow}>
                <dt>Máx. preguntas</dt>
                <dd>{config.maxItems ?? '—'}</dd>
              </div>
              <div className={styles.faqAdmin__gridRow}>
                <dt>Ventana (días)</dt>
                <dd>{config.lookbackDays ?? '—'}</dd>
              </div>
              {runResult && (
                <div className={styles.faqAdmin__gridRow}>
                  <dt>Resultado</dt>
                  <dd>
                    {runResult.regenerado === false
                      ? 'Sin preguntas nuevas para procesar.'
                      : (
                        <pre className={styles.faqAdmin__pre}>
                          {JSON.stringify(runResult, null, 2)}
                        </pre>
                      )}
                  </dd>
                </div>
              )}
            </dl>
          ) : (
            <p className={styles.faqAdmin__muted}>
              No se pudo cargar la configuración del job. Habilita el permiso
              público <code className={styles.faqAdmin__code}>find</code> para
              <code className={styles.faqAdmin__code}> Faq-config </code>
              en Strapi (Settings → Users & Permissions → Roles → Public).
              Mientras tanto, el botón de regenerar sigue funcionando.
            </p>
          )}
        </section>

        {/* Sección: acción */}
        <section className={styles.faqAdmin__section}>
          <h2 className={styles.faqAdmin__subtitle}>Regenerar ahora</h2>
          <p className={styles.faqAdmin__muted}>
            Descarga preguntas recientes del chatbot, genera respuestas con IA y
            las guarda como borrador. Puede tardar 1–3 minutos.
          </p>

          <button
            type="button"
            onClick={handleRegenerate}
            disabled={submitting}
            className={styles.faqAdmin__button}
          >
            {submitting ? 'Regenerando…' : 'Regenerar FAQ ahora'}
          </button>

          {submitting && (
            <div className={styles.faqAdmin__loading}>
              <span className={styles.faqAdmin__spinner} aria-hidden="true" />
              <span>{elapsed}s transcurridos — descargando del chatbot, generando con IA, traduciendo…</span>
            </div>
          )}

          {error && (
            <div className={styles.faqAdmin__error}>
              <p>{error}</p>
              <button
                type="button"
                onClick={handleRegenerate}
                className={styles.faqAdmin__linkButton}
              >
                Reintentar
              </button>
            </div>
          )}

          {result && !error && (
            <div className={styles.faqAdmin__success}>
              <p>
                <strong>{result.regenerado === false
                  ? 'Sin novedades.'
                  : 'Regeneración completada.'}</strong>
              </p>
              {result.regenerado === false ? (
                <p className={styles.faqAdmin__muted}>
                  No había preguntas nuevas desde la última vez. El contenido no cambió.
                </p>
              ) : (
                <pre className={styles.faqAdmin__pre}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
              <p className={styles.faqAdmin__muted}>
                Recuerda publicar la página en Strapi para que sea visible.
              </p>
            </div>
          )}
        </section>

        <footer className={styles.faqAdmin__footer}>
          <p className={styles.faqAdmin__muted}>
            Panel interno. La protección por contraseña es del frontend; el
            endpoint de Strapi es público por diseño. Para bloqueo real,
            configura auth en el backend.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default FaqAdmin;
